import React from 'react';
import { useSet } from 'react-use';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { FormValues } from '../../Taxes.meta';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { formatCurrency } from '../../../../CommonUtils/formatNumber';
import { HtmlTable } from '../../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { WorkflowComponentProps } from '../../../../Workflow/Workflow.meta';
import { IconButton } from '../../../../CommonComponents/OPL/IconButton/IconButton';
import { InputNumberMask } from '../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Input } from '../../../../CommonComponents/OPL/Input/Input';
import { getColumnHeaders } from './ItemizedDeductions.meta';
import { Tooltip } from '../../../../CommonComponents/OPL/Tooltip/Tooltip';
import { Icon } from '../../../../CommonComponents/OPL/Icon/Icon';
import { DeductibleExpense, TaxEstimationMode } from '../../api/useTaxesQuery';
import { Lozenge } from '../../../../CommonComponents/OPL/Lozenge/Lozenge';

interface Props {
  workflowProps?: WorkflowComponentProps;
}

export const ItemizedDeductions = ({ workflowProps }: Props) => {
  const formBag = useFormContext<FormValues>();

  const [
    ,
    { has: isEditingExpense, add: addEditingExpense, remove: removeEditingExpense },
  ] = useSet();

  const expensesFieldArray = useFieldArray({
    control: formBag.control,
    name: 'itemizedDeductibleExpenses',
    keyName: 'key',
  });
  const watchFieldArray = formBag.watch('itemizedDeductibleExpenses');
  const controlledFields = expensesFieldArray.fields.map((field, index) => {
    return {
      ...field,
      ...watchFieldArray[index],
    };
  });

  const [
    useDifferentPostRetirementTaxes,
    preRetirementTaxEstimationMode,
    postRetirementTaxEstimationMode,
    useStandardDeductions,
  ] = formBag.watch([
    'useDifferentPostRetirementTaxes',
    'preRetirementTaxEstimationMode',
    'postRetirementTaxEstimationMode',
    'useStandardDeductions',
  ]);

  const showPreRetirementColumn = preRetirementTaxEstimationMode === TaxEstimationMode.Automatic;

  const showPostRetirementColumn =
    useDifferentPostRetirementTaxes &&
    postRetirementTaxEstimationMode === TaxEstimationMode.Automatic;

  const headers = getColumnHeaders(showPreRetirementColumn, showPostRetirementColumn);

  const getIsExpenseInPrePopulatedState = (expense: DeductibleExpense & { key: string }) =>
    expense.isPrepopulatedField &&
    expense.amount === null &&
    expense.postRetirementAmount === null &&
    !isEditingExpense(expense.key);

  const total = controlledFields.reduce((acc, expense) => {
    const isInPrePopulatedState = getIsExpenseInPrePopulatedState(expense);

    const amount = isInPrePopulatedState ? expense.defaultAmount : expense.amount;

    return acc + (amount ?? 0);
  }, 0);

  const postRetirementTotal = controlledFields.reduce((acc, expense) => {
    const isInPrePopulatedState = getIsExpenseInPrePopulatedState(expense);

    const amount = isInPrePopulatedState
      ? expense.defaultPostRetirementAmount
      : expense.postRetirementAmount;

    return acc + (amount ?? 0);
  }, 0);

  return (
    <HtmlTable css="overflow-y: unset" variant={workflowProps && 'bordered'}>
      <table>
        <colgroup>
          <col width="50%" />
          {showPreRetirementColumn && showPostRetirementColumn ? (
            <>
              <col width="20%" />
              <col width="20%" />
            </>
          ) : (
            <col width="40%" />
          )}
          <col width="10%" />
        </colgroup>

        <caption>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <span css={{ marginRight: 8 }}>Itemized Deductible Expense Details</span>
              <Lozenge variantColor="new">Beta</Lozenge>
            </Box>

            <Button
              leftIcon="fas fa-plus"
              variant="subtleActive"
              disabled={useStandardDeductions}
              onClick={() =>
                expensesFieldArray.append({ isPrepopulatedField: false, isCustom: true })
              }
            >
              Add Itemized Deductible Expense
            </Button>
          </Box>
        </caption>

        <thead>
          <tr>
            {headers.map(({ name, key, align, ariaLabel }) => (
              <th key={key} {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}>
                <Text variant="smallCaps" textAlign={align}>
                  {name}

                  {key === 'amount' && (
                    <Tooltip
                      content="The pre-populated amounts shown are the current year's applicable deductible limits and will automatically update each year. If you override these values, your custom inputs will remain fixed for the rest of the plan. You can reset at any time to restore the default calculations."
                      placement="top"
                    >
                      <Icon
                        className="far fa-info-circle"
                        color="neutral.500"
                        fontSize="small"
                        ml="4"
                      />
                    </Tooltip>
                  )}

                  {key === 'postRetirementAmount' && (
                    <Tooltip
                      content="The pre-populated amounts shown in this column are the applicable deductible limits in the first year of the retirement period and will automatically update each subsequent year. If you override these values, your custom inputs will remain fixed for the rest of the applicable planning period. You can reset at any time to restore the default calculation."
                      placement="top"
                    >
                      <Icon
                        className="far fa-info-circle"
                        color="neutral.500"
                        fontSize="small"
                        ml="4"
                      />
                    </Tooltip>
                  )}
                </Text>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {controlledFields.map((expense, index) => {
            const isInPrePopulatedState = getIsExpenseInPrePopulatedState(expense);

            return (
              <tr key={expense.key}>
                <td>
                  {expense.isCustom ? (
                    <Input
                      {...formBag.register(`itemizedDeductibleExpenses.${index}.name`)}
                      aria-label="Expense name"
                      placeholder="Expense name"
                      disabled={useStandardDeductions}
                    />
                  ) : (
                    <Text fontWeight={500}>{expense.name}</Text>
                  )}
                </td>

                {showPreRetirementColumn && (
                  <td>
                    {isInPrePopulatedState ? (
                      <Text fontWeight={500} textAlign="right">
                        {formatCurrency(expense.defaultAmount ?? 0, {
                          mantissa: 2,
                        })}
                      </Text>
                    ) : (
                      <Controller
                        control={formBag.control}
                        name={`itemizedDeductibleExpenses.${index}.amount`}
                        render={({ field: { onChange, ...field } }) => (
                          <InputNumberMask
                            {...field}
                            value={field.value}
                            aria-label="Expense amount"
                            leftIcon="fa fa-dollar-sign"
                            allowNegative={false}
                            onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                            disabled={useStandardDeductions}
                          />
                        )}
                      />
                    )}
                  </td>
                )}

                {showPostRetirementColumn && (
                  <td>
                    {isInPrePopulatedState ? (
                      <Text fontWeight={500} textAlign="right">
                        {formatCurrency(expense.defaultPostRetirementAmount ?? 0, {
                          mantissa: 2,
                        })}
                      </Text>
                    ) : (
                      <Controller
                        control={formBag.control}
                        name={`itemizedDeductibleExpenses.${index}.postRetirementAmount`}
                        render={({ field: { onChange, ...field } }) => (
                          <InputNumberMask
                            {...field}
                            value={field.value}
                            aria-label="Post-retirement amount"
                            leftIcon="fa fa-dollar-sign"
                            allowNegative={false}
                            onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                            disabled={useStandardDeductions}
                          />
                        )}
                      />
                    )}
                  </td>
                )}

                <td style={{ textAlign: 'right' }}>
                  {isInPrePopulatedState && !useStandardDeductions && (
                    <IconButton
                      icon="far fa-pencil"
                      variant="subtleInactive"
                      aria-label="Edit expense"
                      onClick={() => {
                        addEditingExpense(expense.key);

                        if (showPreRetirementColumn) {
                          formBag.setValue(
                            `itemizedDeductibleExpenses.${index}.amount`,
                            expense.defaultAmount,
                            {
                              shouldDirty: true,
                            },
                          );
                        }

                        if (showPostRetirementColumn) {
                          formBag.setValue(
                            `itemizedDeductibleExpenses.${index}.postRetirementAmount`,
                            expense.defaultPostRetirementAmount,
                            {
                              shouldDirty: true,
                            },
                          );
                        }
                      }}
                    />
                  )}

                  {!isInPrePopulatedState && expense.isPrepopulatedField && !useStandardDeductions && (
                    <Tooltip
                      content="Restore the default calculated amount"
                      placement="top"
                      delay={1000}
                    >
                      <IconButton
                        icon="far fa-sync-alt"
                        variant="subtleInactive"
                        aria-label="Restore the default calculated amount"
                        onClick={() => {
                          removeEditingExpense(expense.key);

                          if (showPreRetirementColumn) {
                            formBag.setValue(`itemizedDeductibleExpenses.${index}.amount`, null, {
                              shouldDirty: true,
                            });
                          }

                          if (showPostRetirementColumn) {
                            formBag.setValue(
                              `itemizedDeductibleExpenses.${index}.postRetirementAmount`,
                              null,
                              {
                                shouldDirty: true,
                              },
                            );
                          }
                        }}
                      />
                    </Tooltip>
                  )}

                  {expense.isCustom && !useStandardDeductions && (
                    <IconButton
                      aria-label="Remove expense"
                      variant="subtleInactive"
                      icon="far fa-minus-circle"
                      onClick={() => expensesFieldArray.remove(index)}
                      ml="8"
                    >
                      Remove
                    </IconButton>
                  )}
                </td>
              </tr>
            );
          })}

          <tr style={{ height: 57 }}>
            <td>
              <Text fontWeight={600}>Total</Text>
            </td>

            {showPreRetirementColumn && (
              <td>
                <Text variant="tabular" fontWeight={600} textAlign="right">
                  {formatCurrency(total, { mantissa: 2 })}
                </Text>
              </td>
            )}

            {showPostRetirementColumn && (
              <td>
                <Text variant="tabular" fontWeight={600} textAlign="right">
                  {formatCurrency(postRetirementTotal, { mantissa: 2 })}
                </Text>
              </td>
            )}

            <td />
          </tr>
        </tbody>
      </table>
    </HtmlTable>
  );
};
